import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, LogOut, Pencil, Trash2, QrCode, Key, MapPin, BarChart2 } from 'lucide-react';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StaffDashboard = () => {
  const navigate = useNavigate();
  const { token, username, logout } = useAuth();
  const [exhibits, setExhibits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchExhibits();
  }, []);

  const fetchExhibits = async () => {
    try {
      const response = await axios.get(`${API}/exhibits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExhibits(response.data);
    } catch (error) {
      toast.error('Failed to load exhibits');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublish = async (exhibitId, currentStatus) => {
    try {
      await axios.patch(
        `${API}/exhibits/${exhibitId}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(currentStatus ? 'Exhibit unpublished' : 'Exhibit published');
      fetchExhibits();
    } catch (error) {
      toast.error('Failed to update publish status');
    }
  };

  const handleDelete = async (exhibitId) => {
    try {
      await axios.delete(`${API}/exhibits/${exhibitId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Exhibit deleted');
      fetchExhibits();
    } catch (error) {
      toast.error('Failed to delete exhibit');
    }
  };

  const handleGenerateQR = async (exhibitId) => {
    try {
      await axios.post(
        `${API}/exhibits/${exhibitId}/generate-qr`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('QR code generated');
      fetchExhibits();
    } catch (error) {
      toast.error('Failed to generate QR code');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/staff/login');
  };

  const filteredExhibits = exhibits.filter(exhibit => 
    exhibit.title_tc.toLowerCase().includes(searchQuery.toLowerCase()) ||
    exhibit.title_en.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background" data-testid="staff-dashboard-page">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-heading font-bold">Exhibit Management</h1>
              <p className="text-sm text-muted-foreground">Welcome, {username}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={() => navigate('/staff/statistics')}
                variant="outline"
                size="sm"
                data-testid="visitor-statistics-button"
              >
                <BarChart2 className="w-4 h-4 mr-2" />
                Statistics
              </Button>
              <Button
                onClick={() => navigate('/staff/zones')}
                variant="outline"
                size="sm"
                data-testid="manage-zones-button"
              >
                <MapPin className="w-4 h-4 mr-2" />
                Zones
              </Button>
              <Button
                onClick={() => navigate('/staff/change-password')}
                variant="outline"
                size="sm"
                data-testid="change-password-button"
              >
                <Key className="w-4 h-4 mr-2" />
                Password
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <Input
            placeholder="Search exhibits..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-xs"
            data-testid="search-input"
          />
          <Button
            onClick={() => navigate('/staff/exhibit/create')}
            data-testid="create-exhibit-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create New Exhibit
          </Button>
        </div>

        {/* Exhibits Table */}
        {loading ? (
          <div className="text-center py-12" data-testid="loading-state">Loading...</div>
        ) : filteredExhibits.length === 0 ? (
          <div className="text-center py-12 border border-border rounded-lg" data-testid="empty-state">
            <p className="text-muted-foreground">No exhibits found</p>
          </div>
        ) : (
          <div className="bg-white border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="exhibits-table">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="text-left px-6 py-3 text-sm font-medium">Code</th>
                    <th className="text-left px-6 py-3 text-sm font-medium">Image</th>
                    <th className="text-left px-6 py-3 text-sm font-medium">Title (TC)</th>
                    <th className="text-left px-6 py-3 text-sm font-medium">Status</th>
                    <th className="text-left px-6 py-3 text-sm font-medium">QR Code</th>
                    <th className="text-left px-6 py-3 text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredExhibits.map((exhibit) => (
                    <tr key={exhibit.id} data-testid={`exhibit-row-${exhibit.id}`}>
                      <td className="px-6 py-4">
                        <div className="font-mono text-2xl font-bold text-primary">{exhibit.code || '---'}</div>
                      </td>
                      <td className="px-6 py-4">
                        {exhibit.image_url ? (
                          <img
                            src={`${API}${exhibit.image_url}?t=${exhibit.updated_at ? new Date(exhibit.updated_at).getTime() : Date.now()}`}
                            alt={exhibit.title_tc}
                            className="w-16 h-16 object-cover rounded"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-muted rounded flex items-center justify-center text-xs text-muted-foreground">
                            No image
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium">{exhibit.title_tc}</div>
                        <div className="text-sm text-muted-foreground">{exhibit.title_en}</div>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleTogglePublish(exhibit.id, exhibit.is_published)}
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            exhibit.is_published
                              ? 'bg-green-100 text-green-700'
                              : 'bg-zinc-100 text-zinc-700'
                          }`}
                          data-testid={`publish-toggle-${exhibit.id}`}
                        >
                          {exhibit.is_published ? 'Published' : 'Unpublished'}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        {exhibit.qr_code_url ? (
                          <span className="text-green-600 text-sm" data-testid={`qr-status-${exhibit.id}`}>Generated</span>
                        ) : (
                          <Button
                            onClick={() => handleGenerateQR(exhibit.id)}
                            variant="outline"
                            size="sm"
                            data-testid={`generate-qr-${exhibit.id}`}
                          >
                            <QrCode className="w-4 h-4 mr-1" />
                            Generate
                          </Button>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => navigate(`/staff/exhibit/${exhibit.id}/edit`)}
                            variant="ghost"
                            size="sm"
                            data-testid={`edit-button-${exhibit.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive"
                                data-testid={`delete-button-${exhibit.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Exhibit</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDelete(exhibit.id)}>
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffDashboard;